import { Meridiem, TimeParts, TimeString } from '../../../types/DesignSystem/TimePicker';

/** The stored contract: 24-hour `HH:mm`. Unchanged by § 290. */
export const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * What a user may TYPE. One regex for both readings, because the meridiem is what
 * tells them apart:
 *
 * - with a meridiem (`5:06 pm`, `5.06PM`, `5 p`) the hour is 12-hour, 1-12;
 * - without one (`17:06`, `05:06`) the hour is **24-hour**, 0-23.
 *
 * That rule is not a preference. A bare `05:06` has to mean something, and the value
 * this field stores is 24-hour — so reading an unsuffixed number as 24-hour is the
 * only choice under which what you type and what is stored cannot disagree.
 */
const TYPED = /^\s*(\d{1,2})(?:\s*[:.]\s*(\d{1,2}))?\s*(?:([ap])\s*\.?\s*m?\s*\.?)?\s*$/i;

export const EMPTY_PARTS: TimeParts = { h12: '', mm: '', mer: '' };

const pad = (n: number): string => String(n).padStart(2, '0');

/** `'05'` + `'PM'` → `'17'`. Noon and midnight are the two the naive `h + 12` gets wrong. */
export const to24 = (h12: string, mer: Meridiem): string => {
  const h = Number(h12) % 12;
  return pad(mer === 'PM' ? h + 12 : h);
};

/** `'17:06'` → `{ h12: '05', mm: '06', mer: 'PM' }`. Anything unparseable → all empty. */
export const partsFromValue = (value: TimeString | undefined): TimeParts => {
  const m = (value ?? '').match(HH_MM);
  if (!m) return EMPTY_PARTS;
  const h24 = Number(m[1]);
  return { h12: pad(((h24 + 11) % 12) + 1), mm: m[2], mer: h24 < 12 ? 'AM' : 'PM' };
};

/**
 * The § 129 rule, carried forward verbatim: a partial selection is not a time.
 *
 * All THREE parts are required — and the meridiem counts, which is new. Picking "5"
 * and "06" without saying AM or PM leaves a real ambiguity on a field whose label
 * says UTC, so it reports `''` rather than guessing. (Typing can never land here: an
 * unsuffixed number is read as 24-hour, which fixes the meridiem.)
 */
export const valueFromParts = (p: TimeParts): TimeString =>
  p.h12 && p.mm && p.mer ? `${to24(p.h12, p.mer)}:${p.mm}` : '';

/**
 * What the field shows when the user is not typing in it. Missing parts render as
 * `--`, exactly as the two selects this replaced showed `--` for an unpicked half:
 * a half-chosen time must LOOK half-chosen, or the caller's "please select a start
 * time" arrives as a surprise.
 */
export const displayFromParts = (p: TimeParts): string =>
  p.h12 || p.mm || p.mer ? `${p.h12 || '--'}:${p.mm || '--'} ${p.mer || '--'}` : '';

/** Best-effort read of whatever is in the field. Unparseable → nothing chosen. */
export const partsFromTyped = (text: string): TimeParts => {
  if (text.trim() === '') return EMPTY_PARTS;
  const m = text.match(TYPED);
  if (!m) return EMPTY_PARTS;

  const hour = Number(m[1]);
  const minute = m[2] === undefined ? null : Number(m[2]);
  const mer: Meridiem = m[3] ? (m[3].toLowerCase() === 'p' ? 'PM' : 'AM') : '';

  if (minute !== null && minute > 59) return EMPTY_PARTS;

  if (mer) {
    if (hour < 1 || hour > 12) return EMPTY_PARTS;
    return { h12: pad(hour), mm: minute === null ? '' : pad(minute), mer };
  }

  if (hour > 23) return EMPTY_PARTS;
  const derived = partsFromValue(`${pad(hour)}:00`);
  return { ...derived, mm: minute === null ? '' : pad(minute) };
};

/** `1`…`12`, zero-padded — the hour column. */
export const HOUR_OPTIONS: string[] = Array.from({ length: 12 }, (_, i) => pad(i + 1));

/** `00`, `00 + step`, … — the minute column. Typing is never restricted to these. */
export const minuteOptions = (step: number): string[] => {
  const safeStep = Number.isFinite(step) && step >= 1 && step <= 60 ? Math.floor(step) : 1;
  return Array.from({ length: Math.ceil(60 / safeStep) }, (_, i) => pad(i * safeStep));
};

export const MERIDIEM_OPTIONS: Meridiem[] = ['AM', 'PM'];
